// Code to serialize/deserialize state into URL query params
// Intention is to only track UI state we don't wish to preserve on the server
// If that changes then this can be moved somewhere available to both the client and server

import { Injectable } from "@angular/core";

/** Compatible with @angular/router/Params */
export type Params = Record<string, any>;

export interface QueryParamProvider<T> {
  name: string;
  paramToValue: (param: string) => Partial<T>;
  valueToParam: (state: Partial<T>) => string | number | boolean | null | undefined;
  defaultValue?: Partial<T>;
}

/** Translate http query parameters to/from state objects */
@Injectable()
export class HttpQueryManager<T> {
  private paramProviders: QueryParamProvider<Partial<T>>[] = [];

  registerProvider(...providers: QueryParamProvider<Partial<T>>[]) {
    this.paramProviders.push(...providers);
  }

  stateToParams(state: T): Params {
    const params: Params = {};
    this.paramProviders.forEach((pp) => {
      const p = pp.valueToParam(state);
      if (p !== null && p !== undefined) {
        params[pp.name] = p;
      }
    });
    return params;
  }

  defaultState(): T {
    const state: any = {};
    this.paramProviders.forEach((p) => {
      if (p.defaultValue) {
        Object.assign(state, p.defaultValue);
      }
    });
    return state;
  }

  paramsToState(params: Params, includeDefaults: boolean = false, required: string[] = []): Partial<T> {
    required.forEach((r) => {
      if (!(r in params)) {
        throw `Missing required http query parameter: ${r}`;
      }
    });
    const state: any = {};
    this.paramProviders.forEach((p) => {
      if (p.name in params) {
        Object.assign(state, p.paramToValue(params[p.name]));
      } else if (p.defaultValue && includeDefaults) {
        Object.assign(state, p.defaultValue);
      }
    });
    return state;
  }
}
